import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Badge, Button, Text,
  Field, Input, Dropdown, Option,
  
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { DateInput } from '../components/shared/DateInput';
import { NumberInput } from '../components/shared/NumberInput';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import {
  fetchShipments, updateShipmentStatus, createShipment,
  type Shipment, type ShipmentStatus, type ShipmentBox, type CreateShipmentInput,
} from '../api/transport.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
});

const STATUS_COLOR: Record<ShipmentStatus, 'success' | 'warning' | 'danger' | 'subtle'> = {
  PENDING:    'subtle',
  EN_RUTA:    'warning',
  ENTREGADO:  'success',
  CANCELADO:  'danger',
};

const NEXT_STATUS: Record<ShipmentStatus, ShipmentStatus | null> = {
  PENDING:   'EN_RUTA',
  EN_RUTA:   'ENTREGADO',
  ENTREGADO: null,
  CANCELADO: null,
};

const EMPTY_BOX: ShipmentBox = { calibre: '', boxCount: 0, weightKg: 0, grade: 'A' };

interface NewShipmentDialogProps {
  open: boolean;
  onClose: () => void;
}

function NewShipmentDialog({ open, onClose }: NewShipmentDialogProps) {
  const t  = useT();
  const qc = useQueryClient();

  const [loteId, setLoteId] = useState('');
  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setLoteId('') });
  const [destination, setDestination] = useState('');
  const [driver, setDriver] = useState('');
  const [truckPlate, setTruckPlate] = useState('');
  const [transportCompany, setTransportCompany] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [boxes, setBoxes] = useState<ShipmentBox[]>([{ ...EMPTY_BOX }]);
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setLoteId(''); setDestination(''); setDriver(''); setTruckPlate('');
    setTransportCompany(''); setScheduledAt(''); setBoxes([{ ...EMPTY_BOX }]); setError('');
  };

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateShipmentInput = {
        loteId: loteId.trim(),
        destination: destination.trim(),
        driver: driver.trim(),
        truckPlate: truckPlate.trim(),
        transportCompany: transportCompany.trim() || undefined,
        scheduledAt: scheduledAt || undefined,
        boxes: boxes.filter(b => b.calibre.trim() && b.boxCount > 0),
      };
      return createShipment(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transport-shipments'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.transport.createShipmentError),
  });

  const updateBox = (idx: number, patch: Partial<ShipmentBox>) =>
    setBoxes(bs => bs.map((b, i) => i === idx ? { ...b, ...patch } : b));

  const handleSubmit = () => {
    setError('');
    const validBoxes = boxes.filter(b => b.calibre.trim() && b.boxCount > 0);
    const ok = fe.validate({
      loteId: !loteId.trim() && t.transport.loteRequired,
      destination: !destination.trim() && t.transport.destinationRequired,
      boxes: validBoxes.length === 0 && t.transport.boxesRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '560px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="transport"
              voiceExample={t.telegramHelp.transportVoice}
              voiceDesc={t.telegramHelp.transportDesc}
            />
          }>{t.transport.newShipmentTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={t.transport.fieldLoteId} {...fe.props('loteId')}>
                  <SearchableSelect
                    placeholder={t.common.searchLotePlaceholder}
                    options={lotes.map(l => ({ id: l.loteId, label: l.name }))}
                    value={loteId}
                    onChange={(id) => { setLoteId(id); fe.clear('loteId'); }}
                    disabled={mut.isPending}
                    noResultsText={t.common.searchNoResults}
                  />
                </Field>
                <Field label={t.transport.fieldDestination} {...fe.props('destination')}>
                  <Input value={destination} onChange={(_, d) => { setDestination(d.value); fe.clear('destination'); }} placeholder={t.transport.fieldDestinationPlaceholder} disabled={mut.isPending} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={t.transport.fieldDriver}>
                  <Input value={driver} onChange={(_, d) => setDriver(d.value)} disabled={mut.isPending} />
                </Field>
                <Field label={t.transport.fieldTruckPlate}>
                  <Input value={truckPlate} onChange={(_, d) => setTruckPlate(d.value)} disabled={mut.isPending} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={t.transport.fieldTransportCompany}>
                  <Input value={transportCompany} onChange={(_, d) => setTransportCompany(d.value)} disabled={mut.isPending} />
                </Field>
                <Field label={t.transport.fieldScheduledAt}>
                  <DateInput value={scheduledAt} onValueChange={(v) => setScheduledAt(v)} disabled={mut.isPending} />
                </Field>
              </div>

              <Text weight="semibold" size={200}>{t.transport.fieldBoxesTitle}</Text>
              {boxes.map((box, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                  <Field label={t.transport.fieldCalibre} {...(idx === 0 ? fe.props('boxes') : {})}>
                    <Input value={box.calibre} onChange={(_, d) => { updateBox(idx, { calibre: d.value }); fe.clear('boxes'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={t.transport.fieldBoxCount}>
                    <NumberInput value={String(box.boxCount || '')} onValueChange={(v) => { updateBox(idx, { boxCount: parseFloat(v) || 0 }); fe.clear('boxes'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={t.transport.fieldWeightKg}>
                    <NumberInput value={String(box.weightKg || '')} onValueChange={(v) => updateBox(idx, { weightKg: parseFloat(v) || 0 })} disabled={mut.isPending} />
                  </Field>
                  <Field label={t.transport.fieldGrade}>
                    <Dropdown
                      selectedOptions={[box.grade]}
                      value={box.grade}
                      onOptionSelect={(_, d) => updateBox(idx, { grade: d.optionValue as 'A' | 'B' | 'C' })}
                      disabled={mut.isPending}
                    >
                      <Option value="A">A</Option>
                      <Option value="B">B</Option>
                      <Option value="C">C</Option>
                    </Dropdown>
                  </Field>
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={mut.isPending || boxes.length === 1}
                    onClick={() => setBoxes(bs => bs.filter((_, i) => i !== idx))}
                  >
                    {t.transport.removeBoxRow}
                  </Button>
                </div>
              ))}
              <Button size="small" onClick={() => setBoxes(bs => [...bs, { ...EMPTY_BOX }])} disabled={mut.isPending}>
                {t.transport.addBoxRow}
              </Button>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.transport.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function TransportPage() {
  const t = useT();
  const s = useStyles();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const { formatDate } = useLocaleSettings();
  // Roles requeridos por transport.handler.ts: SUPERVISOR, ADMIN, SUPER_ADMIN
  const canWrite = user?.roles.some(r => ['SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;

  const [loteId, setLoteId] = useState('');
  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setLoteId('') });
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ s: Shipment; next: ShipmentStatus } | null>(null);
  const [newShipmentOpen, setNewShipmentOpen] = useState(false);
  const { activeRanchoId } = useRanchoStore();

  const { data, isLoading } = useQuery({
    queryKey: ['transport-shipments', loteId, period, activeRanchoId],
    queryFn:  () => fetchShipments({ loteId: loteId || undefined, period, ranchoId: activeRanchoId }),
  });

  const mutation = useMutation({
    mutationFn: ({ shipment, next }: { shipment: Shipment; next: ShipmentStatus }) =>
      updateShipmentStatus(shipment.shipmentId, shipment.loteId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transport-shipments'] });
      setConfirmOpen(false);
      setConfirmTarget(null);
    },
  });

  const shipments = data?.shipments ?? [];

  function statusLabel(st: ShipmentStatus): string {
    const key = `status${st}` as keyof typeof t.transport;
    return String(t.transport[key] ?? st);
  }

  function requestChange(shipment: Shipment, next: ShipmentStatus) {
    setConfirmTarget({ s: shipment, next });
    setConfirmOpen(true);
  }

  return (
    <div className={s.root}>
      <div className={s.toolbar}>
        <PageTitle>{t.transport.title}</PageTitle>
        {canWrite && (
          <Button appearance="primary" onClick={() => setNewShipmentOpen(true)}>
            {t.transport.addBtn}
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className={s.filters}>
        <Field label={t.transport.filterPeriod}>
          <Input type="month" value={period} onChange={(_, d) => setPeriod(d.value)} />
        </Field>
        <Field label={t.transport.filterLote}>
          <SearchableSelect
            placeholder={t.common.searchLotePlaceholder}
            options={[{ id: '', label: t.transport.filterAllLote }, ...lotes.map(l => ({ id: l.loteId, label: l.name }))]}
            value={loteId}
            onChange={setLoteId}
            noResultsText={t.common.searchNoResults}
          />
        </Field>
      </div>

      {/* Tabla */}
      {isLoading && <Spinner size="small" label={t.transport.loading} />}
      <DataTable<Shipment>
        rows={isLoading ? [] : shipments}
        rowKey={(sh) => sh.shipmentId}
        emptyMessage={isLoading ? '…' : t.transport.noData}
        columns={[
          { key: 'date', header: t.transport.tableDate, render: (sh) => formatDate(sh.scheduledAt) },
          { key: 'destination', header: t.transport.tableDestination },
          { key: 'driver', header: t.transport.tableDriver },
          { key: 'truckPlate', header: t.transport.tablePlate },
          { key: 'kg', header: t.transport.tableKg, render: (sh) => sh.kgTotal.toFixed(1) },
          { key: 'boxCount', header: t.transport.tableBoxes },
          { key: 'gradeA', header: t.transport.tableGradeA, render: (sh) => sh.gradeA.toFixed(1) },
          { key: 'status', header: t.transport.tableStatus, render: (sh) => <Badge appearance="tint" color={STATUS_COLOR[sh.status]}>{statusLabel(sh.status)}</Badge> },
          { key: 'actions', header: t.common.actions, width: 1.6, render: (sh) => {
            const next = NEXT_STATUS[sh.status];
            return (
              <>
                {next && (
                  <Button size="small" onClick={() => requestChange(sh, next)}>
                    {String(t.transport[`mark${next.replace('_', '')}` as keyof typeof t.transport] ?? next)}
                  </Button>
                )}
                {sh.status === 'PENDING' && (
                  <Button size="small" appearance="subtle" style={{ marginLeft: 4 }} onClick={() => requestChange(sh, 'CANCELADO')}>
                    {t.transport.markCancelado}
                  </Button>
                )}
              </>
            );
          } },
        ]}
      />

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(_, d) => setConfirmOpen(d.open)}>
        <DialogSurface>
          <DialogTitle>{t.transport.confirmAction}</DialogTitle>
          <DialogBody>
            <DialogContent>
              {confirmTarget && (
                <p>
                  {confirmTarget.s.destination} → <strong>{statusLabel(confirmTarget.next)}</strong>
                </p>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutation.isPending}
              onClick={() => confirmTarget && mutation.mutate({ shipment: confirmTarget.s, next: confirmTarget.next })}
            >
              {t.common.save}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      <NewShipmentDialog open={newShipmentOpen} onClose={() => setNewShipmentOpen(false)} />
    </div>
  );
}
